import Modal from '@keyslabor/shared/ui/ModalConfirm';
import useModal from '@keyslabor/shared/hooks/useModal.tsx';
import { Button, Divider, Row } from '@keyslabor/shared/ui';
import { useTranslation } from 'react-i18next';
import retryCircle from '@/assets/retryCircle.svg';
import retrySuccessCircle from '@/assets/retrySuccessCircle.svg';
import RetryLogoAnimated from '@/assets/retryLogoAnimated.svg?react';
import React, { FC, PropsWithChildren, SetStateAction, useState } from 'react';
import { useInterval } from 'usehooks-ts';
import styled from 'styled-components';

const ProgressBarSvg = styled.svg`
  --size: 100px;
  --half-size: calc(var(--size) / 2);
  --stroke-width: 7px;
  --radius: calc((var(--size) - var(--stroke-width)) / 2);
  --circumference: calc(var(--radius) * pi * 2);
  --dash: calc((var(--progress) * var(--circumference)) / 100);

  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);

  circle {
    cx: var(--half-size);
    cy: var(--half-size);
    r: var(--radius);
    stroke-width: var(--stroke-width);
    fill: none;
    stroke-linecap: round;

    transform: rotate(-90deg);
    transform-origin: var(--half-size) var(--half-size);
    stroke-dasharray: var(--dash) calc(var(--circumference) - var(--dash));
    transition: stroke-dasharray var(--step-interval) linear 0s;
    stroke: #143980;
  }
`;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const witelistAnimationClasses =
  'animate-pulse-logo delay-[1s] delay-[2s] delay-[3s]';

const RetryFooter: FC<PropsWithChildren> = ({ children }) => (
  <div className={'grid place-content-center relative size-[100px]'}>
    <img
      src={retryCircle}
      role={'presentation'}
      className={'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'}
    />
    <div
      className={
        'absolute font-bold text-[2rem] text-dark-blue left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'
      }
    >
      {children}
    </div>
  </div>
);

const ProcessingFooter: FC<{ timeout: number }> = ({ timeout }) => {
  const [progress, setProgress] = useState<number>(0);
  useInterval(
    () => {
      setProgress((prev) => prev + 1);
    },
    (timeout * 1000) / 100
  );
  return (
    <div className={'grid place-content-center relative size-[100px]'}>
      <ProgressBarSvg
        width="100"
        height="100"
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{
          '--progress': progress,
          '--step-interval': `${timeout / 100}s`,
        }}
      >
        <path
          d="M100 50C100 77.6142 77.6142 100 50 100C22.3858 100 0 77.6142 0 50C0 22.3858 22.3858 0 50 0C77.6142 0 100 22.3858 100 50ZM7 50C7 73.7482 26.2518 93 50 93C73.7482 93 93 73.7482 93 50C93 26.2518 73.7482 7 50 7C26.2518 7 7 26.2518 7 50Z"
          fill="#A3A8CC"
        />
        <circle></circle>
      </ProgressBarSvg>
      <RetryLogoAnimated
        className={
          'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'
        }
      />
    </div>
  );
};

const SuccessFooter: FC = () => (
  <div className={'grid place-content-center relative size-[100px]'}>
    <img
      src={retrySuccessCircle}
      role={'presentation'}
      className={'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'}
    />
    <RetryLogoAnimated
      className={'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'}
    />
  </div>
);

const ErrorFooter: FC<PropsWithChildren> = ({ children }) => {
  const handleRetryClick = () => {
    window.location.reload();
  };
  return (
    <div className={'flex w-full '}>
      <Button
        flexGrow={1}
        kind="large"
        fontSize="14px"
        onClick={handleRetryClick}
      >
        {children}
      </Button>
    </div>
  );
};

type UploadVariant = 'retry' | 'error' | 'success' | 'processing';

type ModalDataOptions = {
  retryAttempt: number;
  variant: UploadVariant;
};

const useGetVariants = (options: ModalDataOptions | null) => {
  const { t } = useTranslation();
  if (!options) return null;

  switch (options.variant) {
    case 'retry':
      return {
        title: t('upload.error_retry_title'),
        message: t('upload.error_retry_message'),
        retryAttempt: options.retryAttempt,
      };
    case 'error':
      return {
        title: t('upload.error_title'),
        message: t('upload.error_message'),
      };
    case 'success':
      return {
        title: t('upload.success_after_retry'),
      };
    case 'processing':
      return {
        title: t('upload.error_retry_loading'),
      };
  }
};

const RetryCountdown: React.FC<{ start: number }> = ({ start }) => {
  const [count, setCount] = useState<number>(start);
  useInterval(() => {
    // Your custom logic here
    setCount((prevCount) => Math.max(prevCount - 1, 1));
  }, 1000);

  return <RetryFooter>{count}</RetryFooter>;
};

function useUploadRetryModal({ uploadTimeout }: { uploadTimeout: number }) {
  const { onToggle, modalProps } = useModal();
  const [data, setData] = useState<ModalDataOptions | null>(null);
  const options = useGetVariants(data);
  const { t } = useTranslation();

  const FooterElement = () => {
    if (!data) return null;

    return (
      <Row width="100%" justifyItems={'center'}>
        <Divider my="20px" />
        {data.variant === 'retry' && <RetryCountdown start={3} />}
        {data.variant === 'processing' && (
          <ProcessingFooter timeout={uploadTimeout} />
        )}
        {data.variant === 'success' && <SuccessFooter />}
        {data.variant === 'error' && (
          <ErrorFooter>{t('upload.error_button_label')}</ErrorFooter>
        )}
      </Row>
    );
  };

  return [
    data && options ? (
      <Modal FooterElement={FooterElement} {...modalProps}>
        {data.variant === 'error' ? (
          <div
            className={
              'font-bold text-center min-h-[96px] grid place-content-center'
            }
          >
            <div className={'text-lg'}>{options.title}</div>
            <svg
              width="50"
              height="50"
              viewBox="0 0 50 50"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={'justify-self-center my-4'}
            >
              <path
                d="M38.1068 28.9773L35.1335 25.8838C36.0235 24.7896 36.7006 23.5791 37.165 22.2525C37.6311 20.9276 37.8641 19.5707 37.8641 18.1818C37.8641 16.6667 37.5607 15.1305 36.9539 13.5732C36.3471 12.016 35.4369 10.6061 34.2233 9.34343L37.1359 6.31313C38.7136 7.95455 39.9175 9.8064 40.7476 11.8687C41.576 13.931 41.9903 16.0354 41.9903 18.1818C41.9903 20.1178 41.6667 22.0008 41.0194 23.8308C40.3722 25.6625 39.4013 27.3779 38.1068 28.9773ZM30.8859 21.4646L22.5728 12.8157C23.0178 12.521 23.5129 12.2896 24.0583 12.1212C24.6052 11.9529 25.1618 11.8687 25.7282 11.8687C27.4272 11.8687 28.8633 12.479 30.0364 13.6995C31.2095 14.92 31.7961 16.4141 31.7961 18.1818C31.7961 18.771 31.7152 19.3392 31.5534 19.8864C31.3916 20.4335 31.1691 20.9596 30.8859 21.4646ZM43.932 34.9747L41.0194 31.9444C42.678 29.9663 43.9013 27.7879 44.6893 25.4091C45.479 23.032 45.8738 20.6229 45.8738 18.1818C45.8738 15.5303 45.3884 12.8889 44.4175 10.2576C43.4466 7.62795 41.9903 5.30303 40.0485 3.28283L42.9612 0.252526C45.267 2.77778 47.017 5.5766 48.2112 8.64899C49.4037 11.7214 50 14.899 50 18.1818C50 21.2542 49.5146 24.2104 48.5437 27.0505C47.5728 29.8923 46.0356 32.5337 43.932 34.9747ZM44.5995 50L28.1553 32.8914V45.9596H23.301V27.8409L13.5922 17.803V18.1818C13.5922 19.697 13.8956 21.2332 14.5024 22.7904C15.1092 24.3476 16.0194 25.7576 17.233 27.0202L14.3204 30.0505C12.7427 28.4091 11.5388 26.5572 10.7087 24.4949C9.88026 22.4327 9.46602 20.3283 9.46602 18.1818C9.46602 17.4663 9.50647 16.7719 9.58738 16.0985C9.66828 15.4251 9.80987 14.7306 10.0121 14.0152L6.91748 10.7955C6.47249 12.016 6.13916 13.2466 5.91748 14.4874C5.69417 15.7298 5.58252 16.9613 5.58252 18.1818C5.58252 20.8333 6.06796 23.4739 7.03884 26.1035C8.00971 28.7348 9.46602 31.0606 11.4078 33.0808L8.49514 36.1111C6.18932 33.5859 4.43932 30.787 3.24515 27.7146C2.05259 24.6423 1.45631 21.4646 1.45631 18.1818C1.45631 16.33 1.64806 14.5093 2.03155 12.7197C2.41667 10.9318 2.99353 9.19613 3.76214 7.51263L0 3.59848L3.39806 0L48.0583 46.4646L44.5995 50Z"
                fill="#A3A8CC"
              />
            </svg>
            {options.message ? (
              <div className={'text-lg'}>{options.message}</div>
            ) : null}
          </div>
        ) : (
          <div
            className={
              'font-bold text-center min-h-[96px] grid place-content-center'
            }
          >
            <div className={'text-2xl'}>{options.title}</div>
            {options.message ? (
              <div className={'text-lg'}>{options.message}</div>
            ) : null}
          </div>
        )}
      </Modal>
    ) : null,
    (modalData: SetStateAction<ModalDataOptions | null>) => {
      setData(modalData);
      onToggle();
    },
  ] as const;
}

export default useUploadRetryModal;
